import { useState, useEffect, useCallback } from 'react';
import { Badge } from 'react-bootstrap';
import Swal from 'sweetalert2';
import ClientEOIService from '../../../../services/ClientEOIService';
import AuthService from '../../../../services/AuthService';

const useClientEOIDetail = ({ eoi, show, onClose, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        desiredStartDate: '',
        contractDuration: 1,
        serviceLocation: '',
        comments: '',
        serviceFee: 0
    });
    const [calculatedAmount, setCalculatedAmount] = useState(0);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadErrors, setUploadErrors] = useState([]);
    const currentUser = AuthService.getCurrentUser();

    // Convertit la date du backend vers le format input
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
            if (Array.isArray(dateString)) {
                const [year, month, day] = dateString;
                return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
            } else if (dateString.includes('T')) {
                const date = new Date(dateString);
                const year = date.getFullYear();
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const day = date.getDate().toString().padStart(2, '0');
                return `${year}-${month}-${day}`;
            } else if (dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
                const [day, month, year] = dateString.split('-');
                return `${year}-${month}-${day}`;
            } else {
                return dateString;
            }
        } catch (error) {
            console.error('Error formatting date for input:', error);
            return '';
        }
    };

    // Convertit la date du format input vers le format backend
    const formatDateForBackend = (dateString) => {
        if (!dateString) return null;
        try {
            const [year, month, day] = dateString.split('-');
            return `${day}-${month}-${year}`;
        } catch (error) {
            console.error('Error formatting date for backend:', error);
            return null;
        }
    };

    // Convertit la date du backend pour l'affichage
    const formatDateForDisplay = (dateString) => {
        if (!dateString) return 'Date inconnue';
        try {
            if (Array.isArray(dateString)) {
                const [year, month, day] = dateString;
                const date = new Date(year, month - 1, day);
                return date.toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                });
            } else if (typeof dateString === 'string' && dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
                const [day, month, year] = dateString.split('-');
                const date = new Date(year, month - 1, day);
                return date.toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                });
            } else if (dateString.includes('T')) {
                const date = new Date(dateString);
                return date.toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                });
            } else {
                return dateString;
            }
        } catch (error) {
            console.error('Error formatting date for display:', error);
            return 'Date invalide';
        }
    };

    const calculateAmount = (duration) => {
        if (!eoi || !eoi.serviceFee) {
            setCalculatedAmount(0);
            return 0;
        }
        const basePrice = eoi.serviceFee;
        const amount = basePrice * duration;
        setCalculatedAmount(amount);
        return amount;
    };

    const handleInputChange = (field, value) => {
        if (field === 'contractDuration') {
            if (value === '') {
                setFormData(prev => ({ ...prev, [field]: '' }));
                return;
            }
            const numValue = parseInt(value);
            if (isNaN(numValue) || numValue < 1) {
                const currentDuration = formData.contractDuration;
                if (currentDuration === '' || currentDuration < 1) {
                    const newFormData = { ...formData, contractDuration: 1 };
                    const newAmount = calculateAmount(1);
                    newFormData.serviceFee = newAmount;
                    setFormData(newFormData);
                } else {
                    const newAmount = calculateAmount(currentDuration);
                    setFormData(prev => ({ ...prev, serviceFee: newAmount }));
                }
                return;
            }
            if (numValue >= 1) {
                const newFormData = { ...formData, contractDuration: numValue };
                const newAmount = calculateAmount(numValue);
                newFormData.serviceFee = newAmount;
                setFormData(newFormData);
            }
            return;
        }
        const newFormData = { ...formData, [field]: value };
        if (field === 'serviceFee' && parseFloat(value) >= 0) {
            const newAmount = calculateAmount(formData.contractDuration);
            newFormData.serviceFee = newAmount;
        }
        setFormData(newFormData);
    };

    const handleContractDurationBlur = () => {
        if (formData.contractDuration === '' ||
            isNaN(parseInt(formData.contractDuration)) ||
            parseInt(formData.contractDuration) < 1) {
            const newFormData = { ...formData, contractDuration: 1 };
            const newAmount = calculateAmount(1);
            newFormData.serviceFee = newAmount;
            setFormData(newFormData);
        } else {
            const validDuration = parseInt(formData.contractDuration);
            if (validDuration >= 1 && validDuration !== formData.contractDuration) {
                const newFormData = { ...formData, contractDuration: validDuration };
                const newAmount = calculateAmount(validDuration);
                newFormData.serviceFee = newAmount;
                setFormData(newFormData);
            }
        }
    };

    const validateFile = (file) => {
        if (!file.type.startsWith('image/')) {
            throw new Error(`"${file.name}" : Seules les images sont autorisées`);
        }
        if (file.size > 10 * 1024 * 1024) {
            throw new Error(`"${file.name}" : La taille ne doit pas dépasser 10 MB`);
        }
        const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
        const fileExt = file.name.split('.').pop().toLowerCase();
        if (!allowedExtensions.includes(fileExt)) {
            throw new Error(`"${file.name}" : Format non accepté (JPG, PNG, GIF, WEBP)`);
        }
        return true;
    };

    const processFiles = async (files) => {
        if (uploadedFiles.length + files.length > 3) {
            Swal.fire('Erreur', 'Vous ne pouvez pas ajouter plus de 3 images.', 'error');
            return;
        }
        const newErrors = [];
        const validFiles = [];
        for (const file of files) {
            try {
                validateFile(file);
                validFiles.push(file);
            } catch (error) {
                newErrors.push(error.message);
            }
        }
        if (newErrors.length > 0) {
            setUploadErrors(newErrors);
            Swal.fire('Erreur', newErrors.join('\n'), 'error');
            setTimeout(() => setUploadErrors([]), 5000);
        }
        if (validFiles.length > 0) {
            setUploadedFiles(prev => [...prev, ...validFiles]);
        }
    };

    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files);
        await processFiles(files);
        event.target.value = '';
    };

    const removeFile = (index) => {
        setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const getMediaUrl = (media) => {
        return media.urlPresigned || media.url;
    };

    const extractErrorMessage = (error) => {
        if (error.response?.data?.message) {
            return error.response.data.message;
        }
        if (error.response?.data?.details) {
            return error.response.data.details;
        }
        if (error.message) {
            return error.message;
        }
        return 'Une erreur est survenue lors de la mise à jour.';
    };

    const buildMediaSubscriptionsPayload = () => {
        const existingMediaSubscriptions = (eoi.mediaSubscriptions || []).map((media) => ({
            id: media.id,
            name: media.name,
            username: media.username
        }));
        const newMediaSubscriptions = uploadedFiles.map((file, index) => ({
            name: file.name,
            username: currentUser?.username,
            createdBy: currentUser?.username,
            fileIndex: index
        }));
        return [...existingMediaSubscriptions, ...newMediaSubscriptions];
    };

    const handleSave = async () => {
        if (!eoi || !currentUser) {
            Swal.fire('Erreur', 'Données manquantes pour la mise à jour', 'error');
            return;
        }
        setIsLoading(true);
        try {
            const formattedDate = formatDateForBackend(formData.desiredStartDate);
            const eoiData = {
                comments: formData.comments,
                clientId: eoi.client?.id || eoi.clientId,
                professionalId: eoi.professional?.id,
                lastModifiedBy: currentUser.username,
                clientEOIStatus: "PENDING",
                contractDuration: parseInt(formData.contractDuration),
                serviceLocation: formData.serviceLocation,
                desiredStartDate: formattedDate,
                serviceFee: formData.serviceFee,
                mediaSubscriptions: buildMediaSubscriptionsPayload()
            };
            const updatedEOI = await ClientEOIService.updateClientEOI(eoi.id, eoiData, uploadedFiles);
            onUpdate(updatedEOI);
            setIsEditing(false);
            setUploadedFiles([]);
            Swal.fire('Succès !', 'Votre demande a été mise à jour.', 'success');
            // ✅ Fermeture automatique après succès
            onClose();
        } catch (error) {
            console.error('Error updating EOI:', error);
            const errorMessage = extractErrorMessage(error);
            Swal.fire('Erreur !', errorMessage, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelRequest = async () => {
        if (!eoi || !currentUser) return;
        const result = await Swal.fire({
            title: 'Confirmer l\'annulation',
            text: "Êtes-vous sûr de vouloir annuler cette demande ? Cette action est irréversible.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, annuler la demande',
            cancelButtonText: 'Non, garder la demande'
        });
        if (result.isConfirmed) {
            setIsLoading(true);
            try {
                const cancelData = {
                    lastModifiedBy: currentUser.username,
                    userRejected: currentUser.username,
                    rejectionReason: `Demande annulée par l'utilisateur: ${currentUser.username}`,
                    clientEOIStatus: "CANCELLED"
                };
                const updatedEOI = await ClientEOIService.updateClientEOI(eoi.id, cancelData);
                onUpdate(updatedEOI);
                Swal.fire('Annulé !', 'Votre demande a été annulée.', 'success');
                onClose();
            } catch (error) {
                console.error('Error cancelling EOI:', error);
                const errorMessage = extractErrorMessage(error);
                Swal.fire('Erreur !', errorMessage, 'error');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING':
                return <Badge bg="warning" className="rounded-pill px-3 py-2">En attente</Badge>;
            case 'APPROVED':
                return <Badge bg="success" className="rounded-pill px-3 py-2">Accepté</Badge>;
            case 'REJECTED':
                return <Badge bg="danger" className="rounded-pill px-3 py-2">Rejeté</Badge>;
            case 'CANCELLED':
                return <Badge bg="secondary" className="rounded-pill px-3 py-2">Annulé</Badge>;
            default:
                return <Badge bg="secondary" className="rounded-pill px-3 py-2">Inconnu</Badge>;
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount) + ' FCFA';
    };

    const getBasePrice = () => {
        return eoi.serviceFee || 0;
    };

    useEffect(() => {
        if (eoi) {
            const initialServiceFee = eoi.serviceFee || 0;
            const initialContractDuration = eoi.contractDuration || 1;
            const initialCalculatedAmount = initialServiceFee * initialContractDuration;
            setFormData({
                desiredStartDate: formatDateForInput(eoi.desiredStartDate || eoi.professional?.desiredStartDate),
                contractDuration: initialContractDuration,
                serviceLocation: eoi.serviceLocation || '',
                comments: eoi.comments || '',
                serviceFee: initialServiceFee
            });
            setCalculatedAmount(initialCalculatedAmount);
            setUploadedFiles([]);
            setUploadErrors([]);
        }
    }, [eoi]);

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDragging) {
            setIsDragging(true);
        }
    }, [isDragging]);

    const handleDrop = useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const files = Array.from(e.dataTransfer.files);
        await processFiles(files);
    }, [uploadedFiles]);

    const Dropzone = ({ children, ...props }) => (
        <div
            {...props}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            style={{
                border: `2px dashed ${isDragging ? '#28a745' : '#dee2e6'}`,
                borderRadius: '12px',
                padding: '2rem',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backgroundColor: isDragging ? 'rgba(40, 167, 69, 0.05)' : '#f8f9fa',
                minHeight: '150px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
            }}
        >
            {children}
            <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileUpload}
                disabled={isLoading || uploadedFiles.length >= 3}
                style={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0,
                    cursor: 'pointer',
                    width: '100%',
                    height: '100%'
                }}
            />
        </div>
    );

    return {
        isEditing,
        setIsEditing,
        isLoading,
        formData,
        setFormData,
        calculatedAmount,
        setCalculatedAmount,
        uploadedFiles,
        setUploadedFiles,
        uploadErrors,
        setUploadErrors,
        isDragging,
        setIsDragging,
        currentUser,
        getStatusBadge,
        formatDateForDisplay,
        formatDateForInput,
        formatCurrency,
        getBasePrice,
        getMediaUrl,
        handleInputChange,
        handleContractDurationBlur,
        handleFileUpload,
        removeFile,
        handleSave,
        handleCancelRequest,
        Dropzone
    };
};

export default useClientEOIDetail;